import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {object} StockUrgencyRefs
 * @property {HTMLElement} [message] - The visible low-stock text.
 */

/**
 * Updates the red low-stock message when a size/variant is selected.
 *
 * @extends {Component<StockUrgencyRefs>}
 */
class StockUrgency extends Component {
  /** @type {Record<string, { show?: boolean, text?: string }>} */
  #variantMap = {};

  /** @type {Element | null} */
  #section = null;

  /** @type {Element | null} */
  #picker = null;

  connectedCallback() {
    super.connectedCallback();
    this.#variantMap = this.#parseVariantMap();
    this.#section = this.closest('.shopify-section, dialog');
    this.#picker = this.closest('variant-picker');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#picker?.addEventListener('change', this.#onPickerChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#picker?.removeEventListener('change', this.#onPickerChange);
  }

  /**
   * @returns {Record<string, { show?: boolean, text?: string }>}
   */
  #parseVariantMap() {
    const json = this.querySelector('script[type="application/json"]')?.textContent;
    if (!json) return {};

    try {
      return JSON.parse(json);
    } catch {
      return {};
    }
  }

  /**
   * @param {Event} event
   */
  #onPickerChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement || event.target instanceof HTMLInputElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
    const variantId = selectedOption?.dataset?.variantId;
    if (variantId) this.#applyVariant(variantId);
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const next = event.detail.data.html.querySelector(`stock-urgency[data-product-id="${this.dataset.productId}"]`);
    if (next) {
      const nextMap = next.querySelector('script[type="application/json"]')?.textContent;
      if (nextMap) {
        try {
          this.#variantMap = JSON.parse(nextMap);
        } catch {
        }
      }
    }

    const variantId = event.detail.resource?.id;
    if (variantId) this.#applyVariant(String(variantId));
  };

  /**
   * @param {string} variantId
   */
  #applyVariant(variantId) {
    const entry = this.#variantMap[variantId];
    const message = this.refs.message;

    if (!entry?.show || !entry.text) {
      this.hidden = true;
      if (message) message.textContent = '';
      return;
    }

    this.hidden = false;
    if (message) message.textContent = entry.text;
  }
}

if (!customElements.get('stock-urgency')) {
  customElements.define('stock-urgency', StockUrgency);
}
